import { useToast } from '@chakra-ui/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { Cart } from '@getjust/gateway';

import { CART_QUERY_KEY, MUTATION_CART_DELETE_LINE_ITEMS_KEY } from '$src/constants';
import { useBaseApiUrl } from '$src/hooks/client/useBaseApiUrl';
import { useCartIdUpdater } from '$src/hooks/client/useCartIdUpdater';
import { useCart } from '$src/hooks/queries';
import { gateway } from '$src/http';
import { DeleteLineItemsInput } from '$src/pages/api/[shopId]/[cartId]/delete-line-items';

export const useDeleteLineItems = () => {
  const baseUrl = useBaseApiUrl();
  const queryClient = useQueryClient();
  const updateCartId = useCartIdUpdater();
  const toast = useToast();
  const { t } = useTranslation('common');
  const { data: cart } = useCart();

  return useMutation({
    mutationFn: (items: DeleteLineItemsInput) => gateway.post<Cart>(`${baseUrl}/delete-line-items`, items),
    mutationKey: [MUTATION_CART_DELETE_LINE_ITEMS_KEY, baseUrl],
    onSuccess: ({ data }) => {
      updateCartId(data.id);
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, data.id], data);
    },
    onMutate: async ({ lineItemIds }) => {
      // Optimistically remove the line items
      const previousCart = queryClient.getQueryData<Cart>([CART_QUERY_KEY, cart?.id]);
      const newLineItems =
        previousCart?.lineItems?.filter((lineItem) => !lineItemIds.includes(lineItem.id)) ?? [];
      const newCart = { ...previousCart, lineItems: newLineItems };
      // @ts-expect-error - known
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, previousCart?.id], newCart);
      return { previousCart };
    },
    onError: (err, data, context) => {
      // If the mutation fails, roll back to the previous cart state
      toast({
        description: t('checkout_form.error.lineItemsDeleteError'),
        status: 'error',
        position: 'top',
        isClosable: true,
        duration: 5000,
      });
      if (!context?.previousCart) return;
      queryClient.setQueryData<Cart>([CART_QUERY_KEY, context?.previousCart?.id], context?.previousCart);
    },
  });
};
